import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';

interface NoteAndDeedData {
  dealId?: string;
  noteDate: string;
  // Payee (Lender/Beneficiary) - the seller of the note
  payeeName: string;
  payeeAddress: string;
  // Payor (Borrower/Maker) - the person making payments
  payorName: string;
  payorAddress: string;
  // Trustee for the Deed of Trust
  trusteeName: string;
  trusteeAddress: string;
  // Property information
  propertyAddress: string;
  propertyCity: string;
  propertyCounty: string;
  propertyState: string;
  legalDescription: string;
  parcelId: string;
  // Financial terms
  originalPrincipal: string;
  downPayment: string;
  currentBalance: string;
  interestRate: string;
  monthlyPayment: string;
  firstPaymentDate: string;
  maturityDate: string;
  termMonths: string;
  lateFee: string;
  gracePeriodDays: string;
  // Balloon payment
  hasBalloon: boolean;
  balloonAmount: string;
  balloonDueDate: string;
}

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

export function NoteAndDeedOfTrustGenerator() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<any[]>([]);
  const [formData, setFormData] = useState<NoteAndDeedData>(() => {
    const saved = localStorage.getItem('noteAndDeedProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
    return {
      noteDate: new Date().toISOString().split('T')[0],
      payeeName: "",
      payeeAddress: "",
      payorName: "",
      payorAddress: "",
      trusteeName: "",
      trusteeAddress: "",
      propertyAddress: "",
      propertyCity: "",
      propertyCounty: "",
      propertyState: "Texas",
      legalDescription: "",
      parcelId: "",
      originalPrincipal: "",
      downPayment: "",
      currentBalance: "",
      interestRate: "",
      monthlyPayment: "",
      firstPaymentDate: "",
      maturityDate: "",
      termMonths: "",
      lateFee: "50.00",
      gracePeriodDays: "15",
      hasBalloon: false,
      balloonAmount: "",
      balloonDueDate: "",
    };
  });

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    localStorage.setItem('noteAndDeedProgress', JSON.stringify(formData));
  }, [formData]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading deals:', error);
      return;
    }
    setDeals(data || []);
  };

  const loadDealData = async (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    // Calculate maturity date from first payment and term
    let maturityDate = "";
    if (deal.first_payment_date && deal.original_term_months) {
      const firstPayment = new Date(deal.first_payment_date);
      firstPayment.setMonth(firstPayment.getMonth() + deal.original_term_months);
      maturityDate = firstPayment.toISOString().split('T')[0];
    }

    setFormData({
      ...formData,
      dealId,
      payeeName: deal.seller_name || "",
      payeeAddress: deal.seller_address || "",
      payorName: deal.buyer_name || "",
      payorAddress: deal.buyer_address || "",
      propertyAddress: deal.property_address || "",
      propertyCity: extractCity(deal.property_address),
      propertyCounty: "",
      originalPrincipal: deal.sales_price?.toString() || "",
      downPayment: deal.down_payment_amount?.toString() || "",
      currentBalance: deal.current_balance?.toString() || deal.beginning_balance?.toString() || "",
      interestRate: deal.interest_rate?.toString() || "",
      monthlyPayment: deal.payment_amount?.toString() || "",
      firstPaymentDate: deal.first_payment_date || "",
      maturityDate,
      termMonths: deal.original_term_months?.toString() || "",
      hasBalloon: deal.has_balloon_payment || false,
      balloonAmount: deal.balloon_amount?.toString() || "",
      balloonDueDate: deal.balloon_due_date || "",
    });
  };

  const extractCity = (address: string | null): string => {
    if (!address) return "";
    const parts = address.split(',');
    return parts.length > 1 ? parts[1].trim() : "";
  };

  const clearForm = () => {
    const emptyForm: NoteAndDeedData = {
      noteDate: new Date().toISOString().split('T')[0],
      payeeName: "",
      payeeAddress: "",
      payorName: "",
      payorAddress: "",
      trusteeName: "",
      trusteeAddress: "",
      propertyAddress: "",
      propertyCity: "",
      propertyCounty: "",
      propertyState: "Texas",
      legalDescription: "",
      parcelId: "",
      originalPrincipal: "",
      downPayment: "",
      currentBalance: "",
      interestRate: "",
      monthlyPayment: "",
      firstPaymentDate: "",
      maturityDate: "",
      termMonths: "",
      lateFee: "50.00",
      gracePeriodDays: "15",
      hasBalloon: false,
      balloonAmount: "",
      balloonDueDate: "",
    };
    setFormData(emptyForm);
    localStorage.removeItem('noteAndDeedProgress');
    toast({
      title: "Form Cleared",
      description: "All fields have been reset",
    });
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0.00";
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const generateDocument = async () => {
    if (!formData.payeeName || !formData.payorName || !formData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in Payee Name, Payor Name, and Property Address at minimum",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Document Title
            new Paragraph({
              text: "PROMISSORY NOTE",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 }
            }),
            new Paragraph({
              text: "(Secured by Deed of Trust)",
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // Date and Amount Header
            new Paragraph({
              children: [
                new TextRun({ text: formatCurrency(formData.currentBalance || formData.originalPrincipal), bold: true }),
                new TextRun({ text: `\t\t\t\t\t\t${formatDate(formData.noteDate)}` }),
              ],
              spacing: { after: 300 }
            }),

            new Paragraph({
              children: [
                new TextRun({ text: `${formData.propertyCity}, ${formData.propertyState}` }),
              ],
              spacing: { after: 400 }
            }),

            // Promise to Pay
            new Paragraph({
              text: "1. PROMISE TO PAY",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun(`FOR VALUE RECEIVED, the undersigned, `),
                new TextRun({ text: formData.payorName, bold: true }),
                new TextRun(` ("Maker" or "Payor"), whose address is ${formData.payorAddress}, promises to pay to the order of `),
                new TextRun({ text: formData.payeeName, bold: true }),
                new TextRun(` ("Payee" or "Holder"), whose address is ${formData.payeeAddress}, or at such other place as the Holder may designate in writing, the principal sum of `),
                new TextRun({ text: formatCurrency(formData.currentBalance || formData.originalPrincipal), bold: true }),
                new TextRun(`, together with interest on the unpaid principal balance from the date of this Note at the rate of `),
                new TextRun({ text: `${formData.interestRate}%`, bold: true }),
                new TextRun(` per annum.`),
              ],
              spacing: { after: 300 }
            }),

            // Payment Terms
            new Paragraph({
              text: "2. PAYMENT TERMS",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun(`Principal and interest shall be payable in monthly installments of `),
                new TextRun({ text: formatCurrency(formData.monthlyPayment), bold: true }),
                new TextRun(`, or more at Maker's option without penalty, commencing on `),
                new TextRun({ text: formatDate(formData.firstPaymentDate), bold: true }),
                new TextRun(`, and continuing on the same day of each month thereafter until `),
                new TextRun({ text: formatDate(formData.maturityDate), bold: true }),
                new TextRun(`, at which time the entire remaining balance of principal and accrued interest shall be due and payable in full.`),
              ],
              spacing: { after: 200 }
            }),

            // Balloon Payment (if applicable)
            ...(formData.hasBalloon && formData.balloonAmount ? [
              new Paragraph({
                children: [
                  new TextRun({ text: "BALLOON PAYMENT: ", bold: true }),
                  new TextRun(`A final balloon payment in the amount of `),
                  new TextRun({ text: formatCurrency(formData.balloonAmount), bold: true }),
                  new TextRun(` shall be due and payable on or before `),
                  new TextRun({ text: formatDate(formData.balloonDueDate), bold: true }),
                  new TextRun(`.`),
                ],
                spacing: { after: 300 }
              }),
            ] : []),

            // Late Charges
            new Paragraph({
              text: "3. LATE CHARGES",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun(`If any payment is not received by the Holder within `),
                new TextRun({ text: formData.gracePeriodDays, bold: true }),
                new TextRun(` days after the due date, the Maker agrees to pay a late charge of `),
                new TextRun({ text: formatCurrency(formData.lateFee), bold: true }),
                new TextRun(` for each such late payment.`),
              ],
              spacing: { after: 300 }
            }),

            // Prepayment
            new Paragraph({
              text: "4. PREPAYMENT",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: "The Maker may prepay all or any part of the unpaid principal balance at any time without penalty. Any partial prepayment shall be applied first to accrued interest and then to the reduction of principal.",
              spacing: { after: 300 }
            }),

            // Default and Acceleration
            new Paragraph({
              text: "5. DEFAULT AND ACCELERATION",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: "If any payment required by this Note is not paid when due, or if Maker fails to perform any obligation under the Deed of Trust securing this Note, the entire unpaid principal balance, together with all accrued interest, shall, at the option of the Holder, become immediately due and payable without notice or demand.",
              spacing: { after: 300 }
            }),

            // Security
            new Paragraph({
              text: "6. SECURITY",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              children: [
                new TextRun(`This Note is secured by a Deed of Trust of even date herewith on real property located at: `),
                new TextRun({ text: formData.propertyAddress, bold: true }),
                new TextRun(`, in the City of ${formData.propertyCity}, County of ${formData.propertyCounty}, State of ${formData.propertyState}.`),
              ],
              spacing: { after: 200 }
            }),

            ...(formData.legalDescription ? [
              new Paragraph({
                children: [
                  new TextRun({ text: "Legal Description: ", bold: true }),
                  new TextRun(formData.legalDescription),
                ],
                spacing: { after: 200 }
              }),
            ] : []),

            ...(formData.parcelId ? [
              new Paragraph({
                children: [
                  new TextRun({ text: "Parcel ID: ", bold: true }),
                  new TextRun(formData.parcelId),
                ],
                spacing: { after: 200 }
              }),
            ] : []),

            ...(formData.trusteeName ? [
              new Paragraph({
                children: [
                  new TextRun({ text: "Trustee: ", bold: true }),
                  new TextRun(`${formData.trusteeName}${formData.trusteeAddress ? `, ${formData.trusteeAddress}` : ""}`),
                ],
                spacing: { after: 300 }
              }),
            ] : []),

            // Waivers
            new Paragraph({
              text: "7. WAIVERS",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: "The Maker and all endorsers, sureties, and guarantors hereof jointly and severally waive presentment for payment, demand, notice of dishonor, protest, and notice of protest.",
              spacing: { after: 300 }
            }),

            // Governing Law
            new Paragraph({
              text: "8. GOVERNING LAW",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: `This Note shall be governed by and construed in accordance with the laws of the State of ${formData.propertyState}.`,
              spacing: { after: 300 }
            }),

            // Attorneys' Fees
            new Paragraph({
              text: "9. ATTORNEYS' FEES",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: "If any action is brought to enforce this Note, the prevailing party shall be entitled to recover reasonable attorneys' fees and costs.",
              spacing: { after: 400 }
            }),

            // Signature Block
            new Paragraph({
              text: "IN WITNESS WHEREOF, the Maker has executed this Promissory Note as of the date first written above.",
              spacing: { after: 400 }
            }),

            new Paragraph({
              text: "MAKER/PAYOR:",
              spacing: { before: 300, after: 200 }
            }),

            new Paragraph({
              text: "_______________________________________",
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: formData.payorName,
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: formData.payorAddress,
              spacing: { after: 400 }
            }),

            new Paragraph({
              text: "Date: _______________________",
              spacing: { after: 600 }
            }),

            // Payee/Holder Information
            new Paragraph({
              text: "PAYEE/HOLDER:",
              spacing: { before: 300, after: 200 }
            }),

            new Paragraph({
              text: formData.payeeName,
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: formData.payeeAddress,
              spacing: { after: 200 }
            }),
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Promissory_Note_${formData.payorName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      localStorage.removeItem('noteAndDeedProgress');
      toast({
        title: "Success",
        description: "Promissory Note generated successfully. Form progress cleared."
      });
    } catch (error) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: "Failed to generate document",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Note & Deed of Trust Generator</span>
          <Button variant="outline" size="sm" onClick={clearForm}>
            Clear Form
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
          <strong>Note:</strong> This generates a Promissory Note secured by a Deed of Trust. 
          The Payee is the lender/beneficiary (seller of the note), and the Payor is the borrower/maker.
        </div>

        <div className="space-y-2">
          <Label>Load from Deal (Optional)</Label>
          <Select onValueChange={loadDealData}>
            <SelectTrigger>
              <SelectValue placeholder="Select a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.buyer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Note Date</Label>
            <Input
              type="date"
              value={formData.noteDate}
              onChange={(e) => setFormData({ ...formData, noteDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>State</Label>
            <Select 
              value={formData.propertyState} 
              onValueChange={(value) => setFormData({ ...formData, propertyState: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Payee (Lender/Beneficiary) Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Payee (Lender/Beneficiary)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payee Name *</Label>
              <Input
                value={formData.payeeName}
                onChange={(e) => setFormData({ ...formData, payeeName: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Payee Address</Label>
              <Input
                value={formData.payeeAddress}
                onChange={(e) => setFormData({ ...formData, payeeAddress: e.target.value })}
                placeholder="123 Main St, City, TX 78000"
              />
            </div>
          </div>
        </div>

        {/* Payor (Borrower/Maker) Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Payor (Borrower/Maker)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payor Name *</Label>
              <Input
                value={formData.payorName}
                onChange={(e) => setFormData({ ...formData, payorName: e.target.value })}
                placeholder="Jane Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Payor Address</Label>
              <Input
                value={formData.payorAddress}
                onChange={(e) => setFormData({ ...formData, payorAddress: e.target.value })}
                placeholder="456 Oak Ave, City, TX 78000"
              />
            </div>
          </div>
        </div>

        {/* Trustee Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Trustee (Optional)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Trustee Name</Label>
              <Input
                value={formData.trusteeName}
                onChange={(e) => setFormData({ ...formData, trusteeName: e.target.value })}
                placeholder="ABC Title Company"
              />
            </div>
            <div className="space-y-2">
              <Label>Trustee Address</Label>
              <Input
                value={formData.trusteeAddress}
                onChange={(e) => setFormData({ ...formData, trusteeAddress: e.target.value })}
                placeholder="789 Trust Blvd, City, TX 78000"
              />
            </div>
          </div>
        </div>

        {/* Property Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Property Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Property Address *</Label>
              <Input
                value={formData.propertyAddress}
                onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
                placeholder="123 Property Lane, City, TX 78000"
              />
            </div>

            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.propertyCity}
                onChange={(e) => setFormData({ ...formData, propertyCity: e.target.value })}
                placeholder="San Antonio"
              />
            </div>

            <div className="space-y-2">
              <Label>County</Label>
              <Input
                value={formData.propertyCounty}
                onChange={(e) => setFormData({ ...formData, propertyCounty: e.target.value })}
                placeholder="Bexar"
              />
            </div>

            <div className="space-y-2">
              <Label>Parcel ID</Label>
              <Input
                value={formData.parcelId}
                onChange={(e) => setFormData({ ...formData, parcelId: e.target.value })}
                placeholder="12345-678-90"
              />
            </div>
          </div>

          <div className="space-y-2 mt-4">
            <Label>Legal Description</Label>
            <Textarea
              value={formData.legalDescription}
              onChange={(e) => setFormData({ ...formData, legalDescription: e.target.value })}
              placeholder="Lot 1, Block 2, ABC Subdivision, as recorded in Volume X, Page Y..."
              rows={4}
            />
          </div>
        </div>

        {/* Financial Terms Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Financial Terms</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Original Principal</Label>
              <Input
                type="number"
                value={formData.originalPrincipal}
                onChange={(e) => setFormData({ ...formData, originalPrincipal: e.target.value })}
                placeholder="100000.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Down Payment</Label>
              <Input
                type="number"
                value={formData.downPayment}
                onChange={(e) => setFormData({ ...formData, downPayment: e.target.value })}
                placeholder="10000.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Current Balance</Label>
              <Input
                type="number"
                value={formData.currentBalance}
                onChange={(e) => setFormData({ ...formData, currentBalance: e.target.value })}
                placeholder="90000.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Interest Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.interestRate}
                onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                placeholder="8.5"
              />
            </div>

            <div className="space-y-2">
              <Label>Monthly Payment</Label>
              <Input
                type="number"
                value={formData.monthlyPayment}
                onChange={(e) => setFormData({ ...formData, monthlyPayment: e.target.value })}
                placeholder="850.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Term (Months)</Label>
              <Input
                type="number"
                value={formData.termMonths}
                onChange={(e) => setFormData({ ...formData, termMonths: e.target.value })}
                placeholder="360"
              />
            </div>

            <div className="space-y-2">
              <Label>First Payment Date</Label>
              <Input
                type="date"
                value={formData.firstPaymentDate}
                onChange={(e) => setFormData({ ...formData, firstPaymentDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Maturity Date</Label>
              <Input
                type="date"
                value={formData.maturityDate}
                onChange={(e) => setFormData({ ...formData, maturityDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Grace Period (Days)</Label>
              <Input
                type="number"
                value={formData.gracePeriodDays}
                onChange={(e) => setFormData({ ...formData, gracePeriodDays: e.target.value })}
                placeholder="15"
              />
            </div>

            <div className="space-y-2">
              <Label>Late Fee</Label>
              <Input
                type="number"
                value={formData.lateFee}
                onChange={(e) => setFormData({ ...formData, lateFee: e.target.value })}
                placeholder="50.00"
              />
            </div>
          </div>
        </div>

        {/* Balloon Payment Section */}
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3 text-primary">Balloon Payment (Optional)</h3>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="hasBalloon"
              checked={formData.hasBalloon}
              onChange={(e) => setFormData({ ...formData, hasBalloon: e.target.checked })}
              className="h-4 w-4"
            />
            <Label htmlFor="hasBalloon">This note has a balloon payment</Label>
          </div>
          
          {formData.hasBalloon && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Balloon Amount</Label>
                <Input
                  type="number"
                  value={formData.balloonAmount}
                  onChange={(e) => setFormData({ ...formData, balloonAmount: e.target.value })}
                  placeholder="50000.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Balloon Due Date</Label>
                <Input
                  type="date"
                  value={formData.balloonDueDate}
                  onChange={(e) => setFormData({ ...formData, balloonDueDate: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        <Button onClick={generateDocument} className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Generate Promissory Note
        </Button>
      </CardContent>
    </Card>
  );
}
